import { useContext, useEffect, useState } from 'react';
import { ImagesContext } from '../../contexts/ImagesContext';
import ModalComponent from '../../components/ModalComponent';
import BlocksContainerExport from '../../components/Blocks/Core/BlocksContainerExport';
import { PublishContext } from '../../contexts/PublishContext';

interface Props {
  open: boolean,
  pageId: string
  pageTitle: string
  isPublished: boolean
  hasSubpages: boolean
  handleClose: () => void
  handleSave: () => void
}

const Index = (props: Props) => {
  const {
    open, pageTitle, pageId, isPublished, hasSubpages, handleClose: incomingHandleClose, handleSave: incomingHandleSave,
  } = props;

  const { isInCooldown, message, setIsInCooldown } = useContext(PublishContext);
  const { isLoading: isFetching } = useContext(ImagesContext);
  const [canPublish, setCanPublish] = useState(false);
  const [loadingVideos, setLoadingVideos] = useState(false);

  const handleClose = () => {
    setIsInCooldown(false);
    incomingHandleClose();
  };

  const handleSave = async () => {
    setCanPublish(false);
    await incomingHandleSave();
    setCanPublish(true);
  };

  const loadVideosConcurrently = async (videoElements: HTMLVideoElement[]) => {
    await Promise.all(videoElements.map((video) => {
      if (video.readyState < 2) {
        return new Promise((resolve) => {
          video.addEventListener('loadeddata', () => {
            resolve(true);
          }, { once: true });
        });
      }

      return Promise.resolve(true);
    }));

    setLoadingVideos(false);
  };

  // effects
  useEffect(() => {
    if (isFetching) return;

    // setup
    setLoadingVideos(true);
    const videoElements = document.querySelectorAll('video');

    // if there is no video, set loading to false
    if (videoElements.length === 0) {
      setLoadingVideos(false);
    } else {
      // foreach video element, check if video is loaded
      loadVideosConcurrently(Array.from(videoElements) as HTMLVideoElement[]);
    }
  }, [isFetching]);

  useEffect(() => {
    setCanPublish(false);

    const timer = setTimeout(() => {
      setCanPublish(!isFetching);
    }, 1000);

    return () => clearTimeout(timer);
  }, [isFetching, open]);

  useEffect(() => {
    setCanPublish(canPublish);
  }, [canPublish, setCanPublish]);

  const verifyIfIsLoading = (): boolean => {
    if (!canPublish) {
      return true;
    }

    if (isFetching) {
      return true;
    }

    if (loadingVideos) {
      return true;
    }

    return false;
  };

  return (
    <ModalComponent
      title={!isPublished ? 'ページ公開' : 'ページ非公開'}
      confirmButtonText={!isPublished ? 'ページを公開にする' : 'ページを非公開にする'}
      loading={verifyIfIsLoading()}
      onConfirm={handleSave}
      show={open}
      backdrop="static"
      keyboard={false}
      closeModal={handleClose}
      centered
      animation={false}
    >
      {isInCooldown && (
        <div className="alert alert-warning mb-3" style={{ fontSize: '0.8rem' }} role="alert">
          {message}
        </div>
      )}
      {!isPublished && (
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <span
            style={{
              display: 'contents',
              fontSize: '2rem',
              color: '#000',
              width: '100%',
            }}
          >
            プレビュー
          </span>
          <div style={{
            overflowY: 'auto',
            minHeight: '30vh',
            maxHeight: '50vh',
            opacity: 0.8,
            width: '100%',
            border: '2px solid #000',
            borderRadius: '4px',
            marginBottom: '1rem',
          }}
          >
            <div
              style={{
                pointerEvents: 'none',
              }}
            >
              <BlocksContainerExport pageId={pageId} />
            </div>
          </div>
        </div>
      )}

      <div className="mb-0">
        <div>
          「
          {pageTitle}
          」
          {!isPublished ? 'を公開してもよろしいですか？' : 'を非公開にします。よろしいですか？'}
        </div>
        {(isPublished && hasSubpages) ? (
          <>
            <br />
            <span className="text-danger mt-5 opacity-75">
              ※子ページも含めて非公開されます。
            </span>
          </>
        ) : ''}
      </div>
    </ModalComponent>
  );
};

export default Index;
